import { usePricingStore } from '@/apps/billing-shared/src/store/pricing';
import { isAddOnAvailableByPreSubscribeFeatureFlag } from '@/apps/once/src/util/pre-subscribed-validation';
import getDpBootstrapPromise from '@/global-libs/classic-bootstrap-promise';
import checkFF from '@/lib/feature-flag';
import {
  countryCodes,
  COUNTRY_TO_LOCALE,
  planDetails,
} from '@apps-billing-shared/definitions';
import { isOrganizationTabVisible } from '@apps-billing-shared/util/filter-org-tabs';
import { formatDisplayDateShort } from '@apps-billing-shared/util/format-display-date-short';
import { formatPrice } from '@apps-once/composables/format-price';
import {
  addOnOptionalServices,
  addOns,
  CHANNEL,
  FREE_PLANS,
  ONE_TIME_FEE_LABEL_KEYS,
  SUBSCRIPTION,
} from '@apps-once/definitions';
import { changePlan } from '@apps-once/request/change-plan';
import { loadCompanyStatForCheckout } from '@apps-once/request/load-company-stat';
import { defineStore } from 'pinia';
import { useAddOnStore } from './add-on';
import { useSubscriptionStore } from './subscription';

/**
 * Transforms business data keyed by hostname (the GetAllMyCompanies /
 * GetCompanyStatForCheckout response shape) into data keyed by business id.
 *
 * @param {Object} dataByHostname - Business data organized by hostname
 * @return {Object} Business data organized by business id
 */
function toDataById(dataByHostname) {
  return Object.keys(dataByHostname).reduce((dataById, hostname) => {
    const { Company, Colleagues } = dataByHostname[hostname];
    dataById[Company.Id] = { ...Company, Colleagues };
    return dataById;
  }, {});
}

const useOnceStore = defineStore('once', {
  state: () => ({
    businessId: null,
    isLoading: false,
    data: {},
  }),
  getters: {
    subscription: state => useSubscriptionStore().data[state.hostname],
    isAioBundle: state =>
      !!useSubscriptionStore().data[state.hostname]?.isAioBundle,
    loading: state => state.isLoading,
    hostname: state => state.data[state.businessId]?.Hostname,
    country: state => state.data[state.businessId]?.Country,
    plan: state => state.data[state.businessId]?.Plan, // This is the plan a tenant is currently on
    id: state => state.data[state.businessId]?.Id,
    hostUrl: state => state.data[state.businessId]?.HostUrl,
    portfolio: state => state.data[state.businessId]?.Portfolio,
    additionalNames: state => state.data[state.businessId]?.AdditionalNames,
    domainSuffix: state => state.data[state.businessId]?.DomainSuffix,
    /**
     * The total count of non-archived employees in the system.
     * This is a simple count of all employees who are not archived,
     * regardless of whether they have performed any actions in the system.
     *
     * Contrast with getActiveUserCount() which counts employees who have
     * performed actions in the system (like approving timesheets) within
     * the current billing period (logic differs depending on the active base plan, see: ColleagueService.php).
     *
     * @param {Object} state - The state object containing business data.
     * @return {number} The total count of non-archived employees.
     */
    totalEmployeeCount: state => state.data[state.businessId]?.Colleagues,
    zuoraCurrency: state => state.data[state.businessId]?.currency,
    proTrialDaysRemaining: state =>
      Math.ceil(state.data[state.businessId]?.TrialRemainingFloat),
    channel: state =>
      String(state.data[state.businessId]?.channel ?? '').toUpperCase(),
    enterpriseBilling: state => state.data[state.businessId]?.EnterpriseBilling,
    thirdPartyUpgradeURL: state =>
      state.data[state.businessId]?.ThirdPartyUpgradeURL,
    thirdPartyUpdateUrl: state =>
      state.data[state.businessId]?.ThirdPartyUpdateUrl,
    isFreePlan() {
      return FREE_PLANS.includes(this.plan);
    },
    isPricingAndPackagingPlan() {
      return planDetails[this.plan] != null;
    },
    createdDate() {
      return this.data[this.businessId]?.Created;
    },
    trialEndedAt() {
      return this.data[this.businessId]?.TrialEndedAt;
    },
    availableAddOns: () => {
      const onceStore = useOnceStore();

      const shouldPayrollBeAvailableForUS =
        onceStore.isCountryUS &&
        onceStore.plan !== SUBSCRIPTION.PRO_TRIAL &&
        checkFF('wo-2991-enable-us-payroll-self-serve');

      return useAddOnStore().data.filter(addOn => {
        if (addOn.featureFlag && addOn.featureFlag !== '') {
          if (!checkFF(addOn.featureFlag)) {
            return false;
          }
        }

        return (
          useOnceStore().getAddOnPricing(addOn.addOnId) ||
          (shouldPayrollBeAvailableForUS &&
            addOn.addOnId === addOns.PAYROLL_ADD_ON_ID)
        );
      });
    },
    sortedAvailableAddOns: () => {
      // Sort the available add-ons according to the defined ordering
      const onceStore = useOnceStore();
      const availableAddOns = onceStore.availableAddOns;
      const filteredAddOns = availableAddOns.filter(addOn =>
        isAddOnAvailableByPreSubscribeFeatureFlag(
          addOn,
          onceStore.getFeatureFlag
        )
      );

      return useAddOnStore()
        .sortAddOnsByOrdering(filteredAddOns.map(addOn => addOn.addOnId))
        .map(addOnId =>
          filteredAddOns.find(addOn => addOn.addOnId === addOnId)
        );
    },
    sortedActiveAddOnsByFrequencyAndOrdering: () => {
      // Sort the available add-ons according to the defined billing frequency and ordering
      const onceStore = useOnceStore();
      const activeAddOns =
        onceStore.getCurrentActiveAndCancelledActiveWithFreeAddOns() || [];

      if (
        activeAddOns.length === 0 ||
        !onceStore.availableAddOns ||
        onceStore.availableAddOns.length === 0
      ) {
        return [];
      }

      // A frequency switch keeps the outgoing variant live until its term ends. Rank each
      // add-on by the variant that will keep billing, so a payroll moving to monthly is not
      // held in the annual group by the annual variant it is leaving.
      const ongoingAddOns =
        useSubscriptionStore().collapseToOngoingFrequencyVariant(
          onceStore.hostname,
          activeAddOns
        );

      const activeAddOnsSortedByFrequency =
        useAddOnStore().sortAddOnsByFrequencyAndOrdering(ongoingAddOns) || [];

      if (activeAddOnsSortedByFrequency.length === 0) {
        return [];
      }

      // Deduplicate by addOnId - necessary because getCurrentActiveAndCancelledActiveWithFreeAddOns
      // can return the same add-on in different billing frequencies (e.g., payroll-annual and payroll-standard)
      return activeAddOnsSortedByFrequency
        .map(activeAddOnId => {
          return onceStore.availableAddOns.find(addOn => {
            return (
              activeAddOnId === addOn.addOnId || // Monthly
              activeAddOnId === addOn.equivalentPaymentFrequencyPlans?.annual ||
              activeAddOnId ===
                addOn.equivalentPaymentFrequencyPlans?.annualPaidMonthly
            );
          });
        })
        .filter(
          (addOn, index, array) =>
            addOn &&
            array.findIndex(item => item?.addOnId === addOn.addOnId) ===
              index &&
            isAddOnAvailableByPreSubscribeFeatureFlag(
              addOn,
              onceStore.getFeatureFlag
            )
        );
    },
    hasAvailableAddOns() {
      return !!useOnceStore().availableAddOns?.length;
    },
    currency() {
      return usePricingStore().getCurrency(this.country);
    },
    currencySymbol() {
      return usePricingStore().getCurrencySymbol(this.country);
    },
    currencyIcon() {
      return usePricingStore().getCurrencyIcon(this.country);
    },
    taxRate() {
      return usePricingStore().getTaxRate(this.country);
    },
    locale() {
      return (
        COUNTRY_TO_LOCALE[this.country] || navigator.language.toLowerCase()
      );
    },
    isCountryUS() {
      return this.country === countryCodes.US;
    },
    /**
     * Whether the user can access the Subscriptions (Plans) tab.
     */
    isPlansTabAccessible() {
      return this.isTabAccessible('edit-org-plans');
    },
    /**
     * Whether the user can access the Billing tab.
     */
    isBillingTabAccessible() {
      return this.isTabAccessible('edit-org-billing');
    },
    /**
     * Whether the user can access the Invoices/Statement tab.
     */
    isStatementTabAccessible() {
      return this.isTabAccessible('edit-org-statement');
    },
    /**
     * Whether the user can access the General tab.
     */
    isGeneralTabAccessible() {
      return this.isTabAccessible('edit-org-general');
    },
  },
  actions: {
    /**
     * Loads business data for a given ID
     * Waits for GetAllMyCompanies to complete before loading subscription
     *
     * @param {string} id - The business ID to load
     * @param {boolean} forceReload - Reload the data for the current business
     * @return {Promise<void>}
     */
    async loadBusiness(id, forceReload = false) {
      this.isLoading = true;
      this.businessId = id;

      try {
        // Wait for GetAllMyCompanies BootstrapPromise to resolve
        // This ensures businesses data is loaded before calling GetSubscription
        const bootstrapPromise = getDpBootstrapPromise();
        const businessesData = await bootstrapPromise.get('GetAllMyCompanies');

        // Check if we received valid businesses data
        if (!businessesData || Object.keys(businessesData).length === 0) {
          throw new Error(
            'GetAllMyCompanies completed but returned no business data.'
          );
        }

        this.setBusinesses(businessesData);

        // Verify the specific business ID exists in the loaded data
        if (!this.data[id]) {
          throw new Error(
            `Business ID ${id} not found in GetAllMyCompanies data.`
          );
        }

        // Ensure hostname is available before calling GetSubscription
        if (!this.hostname) {
          throw new Error(
            `Hostname not available for business ID ${id}. Business data may not contain the expected hostname.`
          );
        }

        await useSubscriptionStore().loadSubscription(
          this.hostname,
          this.plan,
          forceReload
        );
      } catch (error) {
        console.error('[Once Store] Failed to load business', {
          businessId: id,
          error,
        });
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Loads business data for the checkout flow without awaiting the
     * all-tenants GetAllMyCompanies stats fan-out.
     *
     * The checkout only needs the target tenant, so this fetches just that
     * tenant's company-stat (GetCompanyStatForCheckout) instead of gating on
     * the whole-portfolio bootstrap promise, whose fan-out scales with the
     * number of tenants the user is associated with. Subscription loading is
     * unchanged.
     *
     * @param {string} id - The business ID to load
     * @param {boolean} forceReload - Reload the data for the current business
     * @return {Promise<void>}
     */
    async loadBusinessForCheckout(id, forceReload = false) {
      this.isLoading = true;
      this.businessId = id;

      try {
        const businessesData = await loadCompanyStatForCheckout(id);

        if (
          !businessesData ||
          businessesData.error ||
          Object.keys(businessesData).length === 0
        ) {
          throw new Error(
            'GetCompanyStatForCheckout returned no business data.'
          );
        }

        this.mergeBusinesses(businessesData);

        if (!this.data[id]) {
          throw new Error(
            `Business ID ${id} not found in GetCompanyStatForCheckout data.`
          );
        }

        if (!this.hostname) {
          throw new Error(
            `Hostname not available for business ID ${id}. Business data may not contain the expected hostname.`
          );
        }

        await useSubscriptionStore().loadSubscription(
          this.hostname,
          this.plan,
          forceReload
        );
      } catch (error) {
        console.error('[Once Store] Failed to load business for checkout', {
          businessId: id,
          error,
        });
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Ensures subscription data is loaded for the current hostname.
     * This checks if data exists or waits for existing load without triggering a new one.
     * Used when we just need to ensure data is loaded before accessing it.
     *
     * @return {Promise<void>} Resolves when subscription data is available
     */
    async ensureSubscriptionDataLoaded() {
      if (!this.hostname) {
        return;
      }
      return useSubscriptionStore().ensureSubscriptionDataLoaded(this.hostname);
    },

    /**
     * Sets business data organized by hostname
     *
     * @param {Object} dataByHostname - Business data organized by hostname
     * @return {void}
     */
    setBusinesses(dataByHostname) {
      this.data = toDataById(dataByHostname);
    },

    /**
     * Merges business data organized by hostname into the current data,
     * preserving any businesses already loaded. Used by the targeted checkout
     * load path which fetches a single tenant.
     *
     * @param {Object} dataByHostname - Business data organized by hostname
     * @return {void}
     */
    mergeBusinesses(dataByHostname) {
      this.data = { ...this.data, ...toDataById(dataByHostname) };
    },

    /**
     * Gets tax details label for a plan
     *
     * @param {number} planId - The plan ID
     * @return {string|null} The tax details label or null if not found
     */
    getTaxDetailsLabel(planId) {
      return usePricingStore().getTaxDetailsLabel(this.country, planId);
    },

    /**
     * Checks if a plan has attached tax
     *
     * @param {number} planId - The plan ID
     * @return {boolean} Whether the plan has tax
     */
    hasPlanAttachedTax(planId) {
      return usePricingStore().hasTaxPrice(this.country, planId);
    },

    /**
     * Gets available add-ons for a specific plan
     *
     * @param {number} planId - The plan ID
     * @return {Array} Array of available add-ons
     */
    getPlanAvailableAddOns(planId) {
      const availableAddOns = useAddOnStore().data.filter(addOn => {
        if (addOn.featureFlag && addOn.featureFlag !== '') {
          if (!checkFF(addOn.featureFlag)) {
            return false;
          }
        }

        const pricingStore = usePricingStore();
        return !!pricingStore.getAddOnPricing(
          this.country,
          planId,
          addOn.addOnId
        );
      });

      // Sort the available add-ons according to the defined ordering
      return useAddOnStore()
        .sortAddOnsByOrdering(availableAddOns.map(addOn => addOn.addOnId))
        .map(addOnId =>
          availableAddOns.find(addOn => addOn.addOnId === addOnId)
        );
    },

    /**
     * Gets feature flags from the subscription
     * @param {string} featureFlagKey - The FF key
     * @return {boolean} Value of FF
     */
    getFeatureFlag(featureFlagKey) {
      return this.subscription?.featureFlags?.[featureFlagKey] || false;
    },

    /**
     * Gets pricing information for an add-on
     *
     * @param {string} addOnId - The add-on ID
     * @param {number} [planId=this.plan] - The plan ID (defaults to current plan)
     * @return {Object} The add-on pricing information
     */
    getAddOnPricing(addOnId, planId = this.plan) {
      const pricingStore = usePricingStore();
      return pricingStore.getAddOnPricing(this.country, planId, addOnId);
    },

    /**
     * Gets the price for an add-on
     *
     * @param {string} addOnId - The add-on ID
     * @param {number} [planId=this.plan] - The plan ID (defaults to current plan)
     * @return {number} The add-on price
     */
    getAddOnPrice(addOnId, planId = this.plan) {
      const pricingStore = usePricingStore();
      const pricingInfo = pricingStore.getAddOnPricing(
        this.country,
        planId,
        addOnId
      );
      if (!pricingInfo?.pricing?.price) {
        return 0;
      }
      return Number(pricingInfo.pricing.price);
    },

    /**
     * Calculates the total annual price for an add-on before tax
     *
     * @param {string} annualAddOnId - The annual add-on ID
     * @return {number} The total annual price before tax
     */
    getAnnualAddOnTotalPricePreTax(annualAddOnId) {
      const prePurchasedUserCount = this.getPrePurchasedUserCountFromPlan();
      const price = this.getAddOnPrice(annualAddOnId);

      return price * prePurchasedUserCount * 12;
    },

    /**
     * Calculates the total monthly price for an add-on before tax
     *
     * @param {string} addOnId - The monthly add-on ID
     * @return {number} The total monthly price before tax
     */
    getMonthlyAddOnTotalPricePreTax(addOnId) {
      const activeUserCount = this.totalEmployeeCount;
      const price = this.getAddOnPrice(addOnId);

      return price * activeUserCount;
    },

    /**
     * Gets the billing period for an add-on
     *
     * @param {string} addOnId - The add-on ID
     * @return {string} The add-on billing period
     */
    getAddOnPeriod(addOnId) {
      return usePricingStore().getAddOnPeriod(this.country, this.plan, addOnId);
    },

    /**
     * Calculates the prorated upfront price for an annual add-on
     *
     * @param {string} addOnId - The add-on ID
     * @param {number} userCount - The number of users
     * @return {number} The prorated upfront price
     */
    getAnnualAddOnProRataUpfrontPrice(addOnId, userCount) {
      let today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');

      today = `${yyyy}-${mm}-${dd}`;
      const annualBasePlanEndDate = this.getAnnualSubscriptionEndDate();
      return usePricingStore().getAnnualAddOnProRataUpfrontPrice(
        this.country,
        this.plan,
        addOnId,
        userCount,
        today,
        annualBasePlanEndDate
      );
    },

    /**
     * Gets the formatted display price for an add-on
     *
     * @param {string} addOnId - The add-on ID
     * @return {string} The formatted add-on price
     */
    getDisplayAddOnPricing(addOnId) {
      const price = this.getAddOnPricing(addOnId)?.pricing?.price;
      return formatPrice(price);
    },

    /**
     * Gets the weekly inactive price for an add-on
     *
     * @param {string} addOnId - The add-on ID
     * @param {string} [planId=this.plan] - The plan ID (defaults to current plan)
     * @return {number|null} The weekly inactive price or null if not set
     */
    getAddOnDisplayWeeklyInactivePrice(addOnId, planId = this.plan) {
      const pricingStore = usePricingStore();
      const priceDetails = pricingStore.getAddOnPricing(
        this.country,
        planId,
        addOnId
      );
      return priceDetails?.pricing?.priceInactive || null;
    },

    /**
     * Gets pricing information for a plan
     *
     * @param {number} planId - The plan ID
     * @return {Object} The plan pricing information
     */
    getPlanPricing(planId) {
      return usePricingStore().getPlanPricing(this.country, planId);
    },

    /**
     * Gets the price for a plan
     *
     * @param {number} planId - The plan ID
     * @return {number} The plan price
     */
    getPlanPrice(planId) {
      return usePricingStore().getPlanPricing(this.country, planId).price;
    },

    /**
     * Gets the weekly inactive price for a plan
     *
     * @param {number} planId - The plan ID
     * @return {number} The weekly inactive price
     */
    getPlanDisplayWeeklyInactivePrice(planId) {
      const pricing = this.getPlanPricing(planId);
      return pricing.weeklyInactiveCharge;
    },

    /**
     * Gets the minimum user count for a plan
     *
     * @param {number} planId - The plan ID
     * @return {number} The minimum user count
     */
    getPlanMinimumUserCount(planId) {
      return usePricingStore().getMinimumUserCount(this.country, planId);
    },

    /**
     * Gets the minimum spend for a plan
     *
     * @param {number} planId - The plan ID
     * @return {number} The minimum spend amount
     */
    getPlanMinimumSpend(planId) {
      return usePricingStore().getPlanMinimumSpend(this.country, planId);
    },

    /**
     * Gets the annual discount percentage for a plan
     *
     * @param {string} annualPlan - The annual plan ID
     * @return {number} The annual discount percentage
     */
    getAnnualDiscountPercentage(annualPlan) {
      return usePricingStore().getAnnualDiscountPercentage(
        this.country,
        annualPlan
      );
    },

    /**
     * Gets the annual discount amount
     *
     * @param {string} annualPlanId - The annual plan ID
     * @param {number} userCount - The number of users
     * @return {number} The annual add-on discount amount
     */
    getAnnualDiscountAmount(annualPlanId, userCount) {
      return usePricingStore().getAnnualDiscountAmount(
        this.country,
        annualPlanId,
        userCount
      );
    },

    /**
     * Gets the annual add-on discount amount
     *
     * @param {string} annualPlanId - The annual plan ID
     * @param {string} annualAddOnId - The annual add-on ID
     * @param {number} userCount - The number of users
     * @return {number} The annual add-on discount amount
     */
    getAnnualAddOnDiscountAmount(annualPlanId, annualAddOnId, userCount) {
      return usePricingStore().getAnnualAddOnDiscountAmount(
        this.country,
        annualPlanId,
        annualAddOnId,
        userCount
      );
    },

    /**
     * Gets the annual add-on discount amount per month
     *
     * @param {string} annualPlanId - The annual plan ID
     * @param {string} annualAddOnId - The annual add-on ID
     * @return {number} The annual add-on discount amount
     */
    getAnnualAddOnMonthlySavingsAmount(annualPlanId, annualAddOnId) {
      return usePricingStore().getAnnualAddOnMonthlySavingsAmount(
        this.country,
        annualPlanId,
        annualAddOnId
      );
    },

    /**
     * Gets the weekly charge for a plan
     *
     * @param {number} planId - The plan ID
     * @return {number} The weekly charge amount
     */
    getWeeklyCharge(planId) {
      return usePricingStore().getWeeklyCharge(this.country, planId);
    },

    /**
     * Gets the weekly inactive charge for a plan
     *
     * @param {number} planId - The plan ID
     * @return {number} The weekly inactive charge amount
     */
    getWeeklyInactiveCharge(planId) {
      return usePricingStore().getWeeklyInactiveCharge(this.country, planId);
    },

    /**
     * Subscribes to an add-on
     *
     * @param {string} addOnId - The add-on ID
     * @param {boolean} [isTrial=false] - Whether this is a trial subscription
     * @param {string} [promoCode=''] - Optional promo code
     * @param {Object} addOns - The add-ons to be used
     * @param {string[]} addOns.fromAddOns - The add-ons the user currently has active, unknown if coming from trial
     * @param {string[]} addOns.toAddOns - The add-ons the user is purchasing
     * @return {Promise} The subscription result
     */
    subscribeToAddOn(
      addOnId,
      isTrial = false,
      promoCode = '',
      addOns = {},
      isChannelCpq = false
    ) {
      return useAddOnStore().subscribeToAddOn(
        this.hostname,
        addOnId,
        isTrial,
        promoCode,
        addOns,
        isChannelCpq
      );
    },

    /**
     * Moves from an annual to a monthly subscription
     *
     * @return {Promise} The subscription result
     */
    moveFromAnnualToMonthly() {
      return useAddOnStore().moveFromAnnualToMonthly(this.hostname);
    },

    /**
     * Cancels an add-on subscription
     *
     * @param {Object} params - The cancellation parameters
     * @param {string} params.addOnId - The add-on ID to cancel
     * @param {boolean} [params.isTrial] - Whether this is cancelling a trial subscription. When omitted, uses stored value from openCancellationModal.
     * @param {boolean} [params.showOutcomeModal=true] - Whether to display the outcome modal after cancellation
     * @param {string} [params.mainReason=null] - The main reason for cancellation
     * @param {string} [params.additionalFeedback=null] - The additional feedback for cancellation
     * @param {string} [params.alternativeSolution=null] - The alternative solution for cancellation
     * @return {Promise} Promise that resolves when cancellation is complete
     */
    cancelAddOn({
      addOnId,
      isTrial,
      showOutcomeModal = true,
      mainReason = null,
      additionalFeedback = null,
      alternativeSolution = null,
    }) {
      const hostname = this.hostname;

      if (this.getFeatureFlag('wo-2614-add-on-downgrade')) {
        return useAddOnStore().cancelAddOn({
          hostname,
          addOnId,
          isTrial,
          showOutcomeModal,
          mainReason,
          additionalFeedback,
          alternativeSolution,
        });
      } else {
        return useAddOnStore().cancelAddOn({
          hostname,
          addOnId,
          isTrial,
          showOutcomeModal,
        });
      }
    },

    /**
     * Gets the subscription status for an add-on
     *
     * @param {string} addOnId - The add-on ID
     * @return {string} The subscription status
     */
    getAddOnSubscriptionStatus(addOnId) {
      return useSubscriptionStore().getAddOnSubscriptionStatus(
        this.hostname,
        addOnId
      );
    },

    /**
     * Gets the pre-purchased user count from the current plan
     *
     * @return {number} The pre-purchased user count
     */
    getPrePurchasedUserCountFromPlan() {
      return useSubscriptionStore().getPrePurchasedUserCount(this.hostname);
    },

    /**
     * Gets the name of the current plan
     *
     * @return {string} The plan name
     */
    getPlanName() {
      return useSubscriptionStore().getPlanName(this.hostname);
    },

    /**
     * Checks if recently converted from annual to monthly
     *
     * @return {boolean} Whether recently converted
     */
    getRecentlyConvertedFromAnnualToMonthly() {
      return useSubscriptionStore().getRecentlyConvertedFromAnnualToMonthly(
        this.hostname
      );
    },

    /**
     * Gets the annual subscription start date
     *
     * @return {string} The subscription start date
     */
    getAnnualSubscriptionStartDate() {
      return useSubscriptionStore().getAnnualSubscriptionStartDate(
        this.hostname
      );
    },

    /**
     * Gets the annual subscription end date
     *
     * @return {string} The subscription end date
     */
    getAnnualSubscriptionEndDate() {
      return useSubscriptionStore().getAnnualSubscriptionEndDate(this.hostname);
    },

    /**
     * Gets the conversion date
     *
     * @return {string} The conversion date
     */
    getConversionDate() {
      return useSubscriptionStore().getConversionDate(this.hostname);
    },

    /**
     * Checks if the annual subscription is ending within the next 31 days
     *
     * @return {boolean}
     */
    isAnnualSubscriptionEndingWithinNext31Days() {
      return useSubscriptionStore().isAnnualSubscriptionEndingWithinNext31Days(
        this.hostname
      );
    },

    /**
     * Checks if the user has active annual add-ons
     *
     * @return {boolean}
     */
    hasActiveAnnualAddOns() {
      return useSubscriptionStore().hasActiveAnnualAddOns(this.hostname);
    },

    /**
     * Gets the next billing date for the plan
     *
     * @return {string} The next billing date
     */
    getPlanNextBillingDate() {
      return useSubscriptionStore().getPlanNextBillingDate(this.hostname);
    },

    /**
     * Gets the count of employees who have performed actions in the system
     * within the current billing period. This is different from totalEmployeeCount
     * which simply counts all non-archived employees.
     *
     * For Flexi plans: Counts employees who have performed actions in the current billing week
     * For Yearly Premium: Counts all currently active employees
     * For other plans: Counts unique employees who have performed actions (like approving timesheets)
     *                 from the 1st of current month until now
     *
     * @return {number} Count of employees who have performed actions
     */
    getActiveUserCount() {
      return useSubscriptionStore().getActiveUserCount(this.hostname);
    },

    /**
     * Checks if an add-on trial is active
     *
     * @param {string} addOnId - The add-on ID
     * @return {boolean} Whether the trial is active
     */
    isAddOnTrialActive(addOnId) {
      return useSubscriptionStore().isAddOnTrialActive(this.hostname, addOnId);
    },

    /**
     * Gets the trial end date for an add-on
     *
     * @param {string} addOnId - The add-on ID
     * @return {string} The trial end date
     */
    getAddOnTrialEndDate(addOnId) {
      return this.plan == SUBSCRIPTION.PRO_TRIAL
        ? formatDisplayDateShort(this.trialEndedAt)
        : useSubscriptionStore().getAddOnTrialEndDate(this.hostname, addOnId);
    },

    /**
     * Gets the trial end date for an add-on in ISO format
     *
     * @param {string} addOnId - The add-on ID
     * @return {string} The trial end date
     */
    getAddOnTrialEndDateISOString(addOnId) {
      return this.plan == SUBSCRIPTION.PRO_TRIAL
        ? this.trialEndedAt
        : useSubscriptionStore().getAddOnTrialEndDateISOString(
            this.hostname,
            addOnId
          );
    },

    /**
     * Gets the remaining trial days for an add-on
     *
     * @param {string} addOnId - The add-on ID
     * @return {number} The remaining trial days
     */
    getAddOnTrialDaysRemaining(addOnId) {
      return this.plan == SUBSCRIPTION.PRO_TRIAL
        ? this.proTrialDaysRemaining
        : useSubscriptionStore().getAddOnTrialDaysRemaining(
            this.hostname,
            addOnId
          );
    },

    /**
     * Checks if an add-on trial is available
     *
     * @param {string} addOnId - The add-on ID
     * @return {boolean} Whether the trial is available
     */
    isAddOnTrialAvailable(addOnId) {
      return useSubscriptionStore().isAddOnTrialAvailable(
        this.hostname,
        addOnId
      );
    },

    /**
     * Gets the visibility state for an add-on (e.g. 'express_interest', 'eligible', 'hidden')
     * This is usually only for US Payroll Monthly/Standard
     *
     * @param {string} addOnId - The add-on ID
     * @return {string|null} The CTA type
     */
    getAddOnVisibilityState(addOnId) {
      return useSubscriptionStore().getAddOnVisibilityState(
        this.hostname,
        addOnId
      );
    },

    /**
     * Gets the express interest URL for an add-on
     *
     * @param {string} addOnId - The add-on ID
     * @return {string|null} The express interest URL
     */
    getAddOnExpressInterestUrl(addOnId) {
      return useSubscriptionStore().getAddOnExpressInterestUrl(
        this.hostname,
        addOnId
      );
    },

    /**
     * Checks if a cancelled add-on is still active
     *
     * @param {string} addOnId - The add-on ID
     * @return {boolean} Whether the cancelled add-on is still active
     */
    isCancelledAddOnStillActive(addOnId) {
      return useSubscriptionStore().isCancelledAddOnStillActive(
        this.hostname,
        addOnId
      );
    },

    /**
     * Gets the termination date for a cancelled add-on
     *
     * @param {string} addOnId - The add-on ID
     * @return {string} The termination date
     */
    getCancelledAddOnTerminationDate(addOnId) {
      if (!addOnId) {
        return null;
      }
      return useSubscriptionStore().getCancelledAddOnTerminationDate(
        this.hostname,
        addOnId
      );
    },

    /**
     * Checks if there is a paid subscription
     *
     * @return {boolean} Whether there is a paid subscription
     */
    hasPaidSubscription() {
      return useSubscriptionStore().hasPaidSubscription(this.hostname);
    },

    /**
     * Gets the trial length for an add-on
     *
     * @param {string} addOnId - The add-on ID
     * @return {number} The trial length in days
     */
    getTrialLength(addOnId) {
      return useSubscriptionStore().getTrialLength(addOnId);
    },

    /**
     * Gets the trial period for an add-on
     *
     * @param {string} addOnId - The add-on ID
     * @return {string} The trial period
     */
    getTrialPeriod(addOnId) {
      return useSubscriptionStore().getTrialPeriod(addOnId);
    },

    /**
     * Gets the trial display billing date
     *
     * @param {string} addOnId - The add-on ID
     * @return {string} The trial display billing date
     */
    getTrialDisplayBillingDate(addOnId) {
      return useSubscriptionStore().getTrialDisplayBillingDate(addOnId);
    },

    /**
     * Checks if the plan is a flexi plan
     *
     * @return {boolean} Whether the plan is flexi
     */
    isFlexi() {
      return useSubscriptionStore().isFlexi(this.plan);
    },

    /**
     * Checks if the plan is a mobile only plan
     *
     * @return {boolean} Whether the plan is mobile only
     */
    isMobileOnly() {
      return useSubscriptionStore().isMobileOnly(this.plan);
    },

    /**
     * Gets the subscription type for an add-on
     *
     * @param {string} addOnId - The add-on ID
     * @return {string} The subscription type
     */
    getAddOnSubscriptionType(addOnId) {
      return useSubscriptionStore().getAddOnSubscriptionType(
        this.hostname,
        addOnId
      );
    },

    /**
     * Gets the current active add-ons for the business
     *
     * @return {string[]}
     */
    getCurrentActiveAddOns() {
      return useSubscriptionStore().getCurrentActiveAddOns(this.hostname);
    },

    /**
     * Gets the current active and cancelled active add-ons for the business
     *
     * @return {string[]}
     */
    getCurrentActiveAndCancelledActiveAddOns() {
      return useSubscriptionStore().getCurrentActiveAndCancelledActiveAddOns(
        this.hostname
      );
    },

    /**
     * Gets the current active, free to plan, and cancelled active add-ons for the business
     * @return {string[]}
     */
    getCurrentActiveAndCancelledActiveWithFreeAddOns() {
      return useSubscriptionStore().getCurrentActiveAndCancelledActiveWithFreeAddOns(
        this.hostname
      );
    },

    /**
     * Changes the plan for the tenant
     *
     * @param {string} accountType - The new plan ID to switch to (e.g. 'Deputy Good', 'Deputy Better', 'Deputy Best')
     * @throws {Error} If the plan change fails
     * @return {Promise} Whether the plan change was successful
     */
    async changePlan(accountType) {
      const response = await changePlan({
        hostname: this.hostname,
        accountType,
      });

      if (!response.success) {
        console.error('Failed to change plan:', response?.error);
        throw new Error(response.error);
      }

      return response;
    },

    /**
     * Gets the pre-purchased user count for the current business
     * @return {number} The pre-purchased user count
     */
    getPrePurchasedUserCount() {
      return useSubscriptionStore().getPrePurchasedUserCount(this.hostname);
    },

    /**
     * Sets the pre-purchased user count for the current business
     * @param {number} count - The pre-purchased user count
     */
    setPrePurchasedUserCount(count) {
      const subscriptionStore = useSubscriptionStore();
      subscriptionStore.data[this.hostname] = {
        ...subscriptionStore.data[this.hostname],
        EMPLOYEE_QUANTITY_PREPURCHASE: count,
      };
    },

    getCsmName() {
      return useSubscriptionStore().getCsmName(this.hostname);
    },

    getCsmEmail() {
      return useSubscriptionStore().getCsmEmail(this.hostname);
    },
    /**
     * Centralised permissions check for organization tabs.
     */
    isTabAccessible(tabId) {
      const isAppStoreApple = this.channel === CHANNEL.APPSTORE_APPLE;
      const isEnterpriseBillingOrThirdParty =
        this.enterpriseBilling === '1' ||
        !!(
          !isAppStoreApple &&
          (this.thirdPartyUpgradeURL || this.thirdPartyUpdateUrl)
        );
      const isPlanEnterprise = this.plan === SUBSCRIPTION.ENTERPRISE;
      const isCpqChannel = this.channel === CHANNEL.CPQ;
      const isResellerXero = this.channel === CHANNEL.RESELLER_XERO;
      const isCpqAddOnTrialSelfServe = this.getFeatureFlag(
        'wo-2737-cpq-add-on-trial-self-serve'
      );
      const isAppleSelfServeEnabled = this.getFeatureFlag(
        'wo-2826-apple-to-deputy-self-serve'
      );

      return isOrganizationTabVisible(tabId, {
        isEnterpriseBillingOrThirdParty,
        isCpqAddOnTrialSelfServe,
        isPlanEnterprise,
        isCpqChannel,
        isAppStoreApple,
        isAppleSelfServeEnabled,
        isFreePlan: this.isFreePlan,
        isResellerXero,
      });
    },

    isUsPayroll(addOnId) {
      return this.isCountryUS && addOnId === addOns.PAYROLL_ADD_ON_ID;
    },
    /**
     * Returns all one-time-fee line items from the payroll pricing quote,
     * each augmented with a `labelKey` field pointing to the translation key
     * in `once:checkout/summary` (or `null` if the identifier is not mapped).
     *
     * The caller is responsible for translating `labelKey` via its own
     * `getTranslations` instance.
     *
     * @return {Array<{ identifier: string, amount: string, labelKey: string | null }>}
     */
    getOneTimeFees() {
      const pricingStore = usePricingStore();
      const oneTime = pricingStore.payrollQuote?.oneTime ?? [];
      return oneTime.map(item => ({
        ...item,
        labelKey: ONE_TIME_FEE_LABEL_KEYS[item.identifier] ?? null,
      }));
    },
    isUsPayrollSelfServeEnabled(addOnId) {
      return (
        this.isUsPayroll(addOnId) &&
        checkFF('wo-2991-enable-us-payroll-self-serve')
      );
    },
    isUsPayrollDynamicPricingEnabled(addOnId) {
      return (
        this.isUsPayroll(addOnId) &&
        checkFF('wo-3058-use-dynamic-pricing-checkout') &&
        checkFF('wo-2991-enable-us-payroll-self-serve')
      );
    },
    /**
     * Gets the optional services available for an add-on, filtered by the
     * tenant's country.
     *
     * @param {string} addOnId - The add-on ID
     * @return {Array<{ key: string, countries: readonly string[] }>} The
     *   optional services available to this tenant for the given add-on.
     */
    getAvailableOptionalServices(addOnId) {
      // US Payroll optional services (e.g. 401(k) integration) are only
      // honored by the checkout submit and summary paths when dynamic pricing
      // is enabled — include401k is forced false and one-time fees are hidden
      // otherwise. Surfacing them in that case would let users select a
      // service the backend won't provision.
      if (
        this.isUsPayroll(addOnId) &&
        !this.isUsPayrollDynamicPricingEnabled(addOnId)
      ) {
        return [];
      }
      const services = addOnOptionalServices[addOnId] ?? [];
      return services.filter(service =>
        service.countries.includes(this.country)
      );
    },
  },
});

export { useOnceStore };
